import { useState, useEffect } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRightIcon, ShieldCheckIcon, CylinderIcon, WalletIcon } from "@phosphor-icons/react";

// Real Indian Oil / Indane brand assets (uploaded by owner).
const INDANE_LOGO_URL = "https://customer-assets-rejwkqb3.emergentagent.net/job_gas-stock-accounts/artifacts/2fd21ssu_b2f93328dedc3d7c86967e492aa03b39-removebg-preview.png";
const INDIAN_OIL_LOGO_URL = "https://customer-assets-rejwkqb3.emergentagent.net/job_gas-stock-accounts/artifacts/8hjjzz28_IndianOilLogo800x600.webp";
const INDANE_CYLINDERS_URL = "https://customer-assets-rejwkqb3.emergentagent.net/job_gas-stock-accounts/artifacts/6ry6fdhh_image.png";

// Inline SVG marks kept as fallback in case network image is blocked.
function IndaneMark({ size = 40 }) {
  return (
    <img
      src={INDANE_LOGO_URL}
      alt="Indane"
      width={size * 1.6}
      height={size}
      style={{ objectFit: "contain" }}
      loading="eager"
    />
  );
}

function IndianOilMark({ size = 40 }) {
  return (
    <img
      src={INDIAN_OIL_LOGO_URL}
      alt="Indian Oil"
      width={size * 1.3}
      height={size}
      style={{ objectFit: "contain", background: "white", padding: 2 }}
      loading="eager"
    />
  );
}

export default function Login() {
  const { user, login, error } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  // Wipe any stale credentials whenever the login page mounts (post-logout, tab reopen, etc.)
  useEffect(() => {
    setEmail(""); setPw("");
    try { sessionStorage.removeItem("last-email"); } catch (e) { console.warn("sessionStorage cleanup failed", e); }
  }, []);

  if (user && user !== false) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email, pw);
    setBusy(false);
    if (ok) nav("/", { replace: true });
    else setPw("");   // clear password on failed attempt
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-brand-blue-tint">
      {/* Left brand pane */}
      <div className="relative hidden lg:flex lg:col-span-3 flex-col justify-between overflow-hidden text-white p-12"
           style={{
             background: "linear-gradient(135deg, #003C71 0%, #002850 55%, #001a33 100%)",
           }}>
        {/* Diagonal orange stripe accents (Indian Oil signature) */}
        <div className="absolute -right-24 top-0 h-full w-72 bg-brand-orange/90 skew-x-[-12deg] opacity-95" />
        <div className="absolute -right-40 top-0 h-full w-24 bg-brand-yellow skew-x-[-12deg] opacity-90" />

        <div className="relative flex items-center gap-4">
          <div className="bg-white/95 px-3 py-2">
            <IndaneMark size={40} />
          </div>
          <IndianOilMark size={44} />
          <div className="border-l border-white/25 pl-4 ml-2">
            <div className="overline text-brand-orange" style={{ fontSize: 10 }}>Authorised Distributor</div>
            <div className="font-display font-black text-white leading-tight" style={{ fontSize: 18 }}>Indane Gas</div>
          </div>
        </div>

        {/* Decorative Indane cylinder family photo — subtle backdrop right side */}
        <img
          src={INDANE_CYLINDERS_URL}
          alt=""
          aria-hidden="true"
          className="absolute right-0 bottom-0 pointer-events-none select-none"
          style={{
            height: "58%",
            maxWidth: "60%",
            objectFit: "contain",
            objectPosition: "bottom right",
            opacity: 0.28,
            filter: "drop-shadow(0 10px 20px rgba(0,0,0,0.4))",
          }}
        />

        <div className="relative max-w-xl">
          <div className="overline text-brand-yellow mb-3">Distributor Console</div>
          <h1 className="font-display font-black leading-[0.95]" style={{ fontSize: 56 }}>
            Shree Sai Sayaji<br />
            <span className="text-brand-orange">Indane</span>
          </h1>
          <p className="mt-6 text-white/75 max-w-md">
            Stock control, delivery-boy oversight, commercial ledgers &amp; daily cash tally — every cylinder,
            every rupee, accounted for.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-6 border-t border-white/15 pt-6">
            <Stat icon={CylinderIcon} k="Stock" v="Real-time" />
            <Stat icon={WalletIcon} k="Payments" v="Cash · UPI · IOC Paid" />
            <Stat icon={ShieldCheckIcon} k="Access" v="Role · Branch" />
          </div>
        </div>

        <div className="relative overline text-white/40" style={{ fontSize: 10 }}>
          © SAYAJI INDANE · Powered by Indian Oil Corporation Ltd.
        </div>
      </div>

      {/* Right form pane */}
      <div className="lg:col-span-2 flex items-center justify-center p-8 bg-white">
        <form
          onSubmit={submit}
          className="w-full max-w-sm space-y-8"
          data-testid="login-form"
          autoComplete="off"
        >
          {/* Compact logo lockup for mobile */}
          <div className="lg:hidden flex items-center gap-3">
            <div className="bg-white border border-slate-200 px-2 py-1">
              <IndaneMark size={30} />
            </div>
            <div>
              <div className="overline text-brand-orange-ink" style={{ fontSize: 10 }}>Authorised Distributor</div>
              <div className="font-display font-black text-brand-blue leading-tight" style={{ fontSize: 16 }}>SAYAJI INDANE</div>
            </div>
          </div>

          <div>
            <div className="overline text-brand-orange-ink mb-3">Sign in</div>
            <h2 className="font-display text-3xl font-bold text-brand-blue">Welcome back.</h2>
            <p className="text-sm text-slate-500 mt-2">Use your owner or staff credentials.</p>
          </div>

          {/* Anti-autofill honeypots — most browsers pre-fill the first email/password field they see. */}
          <div style={{ display: "none" }} aria-hidden="true">
            <input type="email" name="fakeusernameremembered" tabIndex={-1} autoComplete="off" />
            <input type="password" name="fakepasswordremembered" tabIndex={-1} autoComplete="off" />
          </div>

          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="overline text-brand-blue">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                name="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="off"
                spellCheck="false"
                autoCorrect="off"
                autoCapitalize="off"
                className="rounded-none border-slate-300 focus-visible:ring-0 focus-visible:border-brand-blue h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pw" className="overline text-brand-blue">Password</Label>
              <Input
                id="pw"
                data-testid="login-password-input"
                name="login-password"
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                required
                autoComplete="new-password"
                spellCheck="false"
                className="rounded-none border-slate-300 focus-visible:ring-0 focus-visible:border-brand-blue h-11"
              />
            </div>
          </div>

          {error && (
            <div data-testid="login-error" className="text-sm text-[#FF2400] border border-[#FF2400]/30 bg-[#FF2400]/5 px-3 py-2">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={busy}
            data-testid="login-submit-button"
            className="w-full h-11 rounded-none bg-brand-orange hover:bg-brand-orange-ink text-white transition-transform hover:-translate-y-[1px] group"
          >
            <span>{busy ? "Signing in…" : "Sign in"}</span>
            <ArrowRightIcon size={16} weight="bold" className="ml-2 transition-transform group-hover:translate-x-0.5" />
          </Button>

          <div className="border-t border-slate-200 pt-5 text-xs text-slate-500 flex items-center justify-between">
            <span>Authorised access only</span>
            <Link to="/forgot-password" className="uppercase tracking-wider text-xs hover:text-brand-blue" data-testid="forgot-link">Forgot?</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function Stat({ k, v, icon: Icon }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-brand-yellow">
        {Icon && <Icon size={12} />}
        <span className="overline text-white/60" style={{ fontSize: 10 }}>{k}</span>
      </div>
      <div className="font-display font-bold text-lg mt-1">{v}</div>
    </div>
  );
}
